import React, { useState, useEffect, useRef } from 'react';
import { crmMyLeadsApi } from '../../../api/crmApi';
import { searchCustomers } from '../../../api/customerApi';
import '../CRM.css';

/* Extract the array out of common paginated/plain response shapes. */
const pickList = (data) => {
  const keys = ['content', 'data', 'records', 'result', 'list'];
  const stack = [data];
  while (stack.length) {
    const cur = stack.pop();
    if (Array.isArray(cur)) return cur;
    if (cur && typeof cur === 'object') keys.forEach((k) => { if (cur[k] != null) stack.push(cur[k]); });
  }
  return [];
};

const leadLabel = (l) => l?.customerName || l?.company || l?.leadId || l?.name || 'Lead';

const contains = (value, q) => String(value || '').toLowerCase().includes(q);

const matchLead = (l, q) =>
  [l.customerName, l.company, l.mobile, l.leadId, l.name].some((v) => contains(v, q));

const matchCustomer = (c, q) =>
  [c.name, c.mobile, c.contactNumber, c.email].some((v) => contains(v, q));

const MIN_SEARCH_CHARS = 2;

/* Searchable Lead / Customer picker. Fetches results only after the user
   types at least MIN_SEARCH_CHARS characters. */
const LeadCustomerInput = ({ value, label, onChange, onSelect }) => {
  const [text, setText] = useState(value || '');
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [selKey, setSelKey] = useState(null);

  const seqRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => { setText(value || ''); }, [value]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const runSearch = (raw) => {
    clearTimeout(timerRef.current);
    const keyword = raw.trim().toLowerCase();
    if (keyword.length < MIN_SEARCH_CHARS) {
      setLeads([]);
      setCustomers([]);
      setLoading(false);
      return;
    }

    const seq = ++seqRef.current;
    setLoading(true);
    timerRef.current = setTimeout(() => {
      Promise.allSettled([
        crmMyLeadsApi.getAll({ size: 50, search: keyword }),
        searchCustomers({ name: keyword }),
      ]).then(([l, c]) => {
        if (seq !== seqRef.current) return;
        setLeads(l.status === 'fulfilled' ? pickList(l.value?.data).filter((x) => matchLead(x, keyword)) : []);
        setCustomers(c.status === 'fulfilled' ? pickList(c.value?.data).filter((x) => matchCustomer(x, keyword)) : []);
      }).finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
    }, 300);
  };

  const q = text.trim().toLowerCase();
  const leadResults = leads.map((l) => ({
    kind: 'Lead',
    id: l.id,
    label: leadLabel(l),
    sub: [l.company, l.leadId, l.mobile].filter(Boolean).join(' · '),
  }));
  const customerResults = customers.map((c) => ({
    kind: 'Customer',
    id: c.id,
    label: c.name || `Customer ${c.id}`,
    sub: [c.contactNumber || c.mobile, c.email].filter(Boolean).join(' · '),
  }));
  const results = [...leadResults, ...customerResults];

  const pick = (item) => {
    onChange(item.label);
    setText(item.label);
    setSelKey(`${item.kind}-${item.id}`);
    setOpen(false);
    setActive(-1);
    if (onSelect) onSelect(item);
  };

  const commit = () => {
    const t = text.trim();
    setOpen(false);
    setActive(-1);
    if (t !== (value || '')) onChange(t);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[active >= 0 ? active : 0];
      if (open && hit) pick(hit);
      else commit();
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  const searched = q.length >= MIN_SEARCH_CHARS;

  return (
    <div className="crm-field crm-field-full">
      <label>{label}</label>
      <div className="crm-user-search">
        <input
          type="text"
          className="crm-user-search-input"
          value={text}
          placeholder="search leads or customers..."
          onChange={(e) => { onChange(e.target.value); setText(e.target.value); setOpen(true); setActive(-1); runSearch(e.target.value); }}
          onFocus={() => { setOpen(true); setActive(-1); }}
          onBlur={commit}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck="false"
        />
        {text && (
          <button
            type="button"
            className="crm-user-search-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setText(''); onChange(''); setOpen(true); setActive(-1); runSearch(''); }}
            aria-label="Clear lead or customer"
            title="Clear"
          >×</button>
        )}
        {open && (
          <div className="crm-user-results" role="listbox" aria-label={`${label} options`}>
            {loading && (
              <div className="crm-user-results-empty">Searching leads & customers...</div>
            )}
            {!loading && !searched && (
              <div className="crm-user-results-empty">Type to search...</div>
            )}
            {!loading && searched && results.length === 0 && (
              <div className="crm-user-results-empty">No matching leads or customers</div>
            )}
            {!loading && results.map((item, i) => (
              <div
                key={`${item.kind}-${item.id}`}
                role="option"
                className={`crm-user-result ${i === active ? 'active' : ''} ${selKey === `${item.kind}-${item.id}` ? 'selected' : ''}`}
                aria-selected={selKey === `${item.kind}-${item.id}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(item)}
              >
                <span className="crm-user-result-name">{item.label}</span>
                <span className="crm-user-result-meta">
                  <span className="crm-user-result-kind">{item.kind}</span>
                  {item.sub && <span className="crm-user-result-sub">{item.sub}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCustomerInput;